import {
  FunctionComponent,
  useMemo,
  useState,
  SyntheticEvent,
  memo,
  useCallback,
} from 'react';
import { Loader } from '~components/Loader';
import Currency from '~components/Currency';
import { Price } from '~ui/index';
import { Link, useNavigate } from 'react-router-dom';

import {
  Event as EventTypes,
  EventUser as EventUserTypes,
  Item as ItemTypes,
  Profile as ProfileTypes,
} from '~types';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import {
  useGetProfileEventProgress,
  useGetEventUsers,
  useConfig
} from '~components/hooks';

interface Props {
  getEvent: EventTypes;
}

interface UserProps {
  getEventUser: EventUserTypes;
  getPrize: ItemTypes;
  getProfile: ProfileTypes;
  index: number;
}

const User: FunctionComponent<UserProps> = memo(
  ({ getEventUser, index, getPrize, getProfile }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const getConfig = useConfig();

    const goUser = useCallback(
      (event: SyntheticEvent) => {
        event.preventDefault();

        if (getEventUser) {
          navigate(`/user/${getEventUser?.getUser?.id}`);
        }
      },
      [getEventUser],
    );

    return (
      <div className="breezy-table-row">
        <div className="breezy-table-col">#{index + 6}</div>
        <div className="breezy-table-col">
          <Link
            to={`/search/${getPrize.id}`}
            className="top-drop-item"
            data-rar={getPrize?.color}
          >
            {getPrize.isStatTrak && (
              <div className="stattrack">
                <span className="fas fa-star" /> StatTrak™
              </div>
            )}
            <div className="honey"></div>
            <div
              className="drop-img"
              style={{
                backgroundImage: `url('${getPrize.getImage}/200x200')`,
              }}
            ></div>
          </Link>
          <div className="case-name">
            {getPrize.getName} {getPrize?.quality && `- ${getPrize?.quality}`}
          </div>
        </div>
        <div className="breezy-table-col">
          <div className="breezy-table-user">
            <div className="breezy-table-user-ava" onClick={goUser}>
              <img
                src={
                  getEventUser?.getUser?.avatar ||
                  'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAIAAAgABJ60+RsAAAAASUVORK5CYII='
                }
                alt={getEventUser?.getUser?.userName || '-'}
              />
            </div>
            <div className="breezy-table-user-name">
              <span>{getEventUser?.getUser.userName || '-'}</span>
              {getProfile && getEventUser?.getUser?.id === getProfile?.id ? (
                <span className="you">({t('You')})</span>
              ) : null}
            </div>
          </div>
        </div>
        {getEventUser?.spent && (
          <div className="breezy-table-col">
            <div className="breezy-table-user">
              <div className="breezy-table-user-ava" onClick={goUser}>
                <img
                  src={
                    getEventUser?.getUser?.avatar ||
                    'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAIAAAgABJ60+RsAAAAASUVORK5CYII='
                  }
                  alt={getEventUser?.getUser?.userName || '-'}
                />
              </div>
              <div className="breezy-table-user-name">
                <span>{getEventUser?.getUser.userName || '-'}</span>
                {getProfile && getEventUser?.getUser?.id === getProfile?.id ? (
                  <span className="you">({t('You')})</span>
                ) : null}
              </div>
            </div>
            <div className="cost">
              {getConfig.isEU ? (
                <>
                  <span>
                    <Currency />
                  </span>
                  <span>
                    <Price sum={getEventUser?.spent} ceil />
                  </span>
                </>
              ) : (
                <>
                  <span>
                    <Price sum={getEventUser?.spent} ceil />
                  </span>
                  <span>
                    <Currency />
                  </span>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    );
  },
);

export const UsersLists: FunctionComponent<Props> = ({ getEvent }) => {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const [count, setCount] = useState(30);
  const { loading, error, data } = useGetProfileEventProgress(getEvent.id);

  const getEventUsers = useGetEventUsers({
    eventId: getEvent.id,
    filters: { offset: 5, limit: getEvent?.totalRank },
  });

  const getPrizes = useMemo(() => {
    return _.take(_.slice(getEvent?.getPrizes, 5), count);
  }, [getEvent?.getPrizes, count]);

  const onClick = () => {
    setCount(count + 15);
  };

  if (loading) {
    return <Loader />;
  } else if (error) {
    return <>Progress | Error! {error.message}</>;
  }

  const { getProfile } = data;

  return (
    <div className="breezy-table-wrap">
      <div className="breezy-table-title">
        {t(
          getEvent?.props[language]?.tableTitle ||
            'breezy-table-wrap breezy-table-title',
        )}
      </div>
      <div className="breezy-table">
        <div className="breezy-table-head">
          <div className="breezy-table-row">
            <div className="breezy-table-col">
              {t('breezy-table-wrap breezy-table-row 1')}
            </div>
            <div className="breezy-table-col">
              {t('breezy-table-wrap breezy-table-row 2')}
            </div>
            <div className="breezy-table-col">
              {t('breezy-table-wrap breezy-table-row 3')}
            </div>
            <div className="breezy-table-col">
              {t('breezy-table-wrap breezy-table-row 4')}
            </div>
          </div>
        </div>
        <div className="breezy-table-body">
          {_.map(getPrizes, (getPrize, index) => {
            const getEventUser = _.get(getEventUsers?.getEventUsers, [index]);

            return (
              <User
                key={getPrize.id}
                getEventUser={getEventUser}
                index={index}
                getPrize={getPrize}
                getProfile={getProfile}
              />
            );
          })}
        </div>
      </div>
      {count < getEvent?.totalRank && (
        <div className="breezy-table-load-more">
          <button className="b-violet" onClick={onClick}>
            <div className="icon material-icons">refresh</div>
            <span>{t('Show more')}</span>
          </button>
        </div>
      )}
    </div>
  );
};
